import React, { useState,useEffect, useRef } from 'react';
import "./SettingsPage.css";
import { useSites } from '../../contexts/SitesContext';
import SettingsSiteRow from './sites/SettingsSiteRow';
import DownloadInvoicesModal from './billing/DownloadInvoicesModal';
import InvoiceTemplateModal from './billing/InvoiceTemplateModal';
import CancelSubscriptionModal from './billing/CancelSubscriptionModal';
import ChangeCardModal from './billing/ChangeCardModal';
import DeleteSiteModal from './sites/DeleteSiteModal';
import SubscriptionsModal from './billing/SubscriptionsModal';
import PaymentMethodCard from './billing/PaymentMethodCard';
import PlanCard from './billing/PlanCard';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import EditSiteModal from './sites/EditSiteModal';
import { useMessage } from '../../contexts/MessageContext';
import { copyText } from '../../shared/lib/clipboard';
import { tiers } from '../../shared/config/tiers';
import Separator from '../../shared/components/Separator';
import { useMobile } from '../../contexts/MobileContext';
import { useTheme } from '../../contexts/ThemeContext';
import AddSiteModal from './sites/AddSiteModal';
import { gridTemplateSettings, ListHeader, useViewport } from '../../shared/components/tableUtils';

import { ReactComponent as Spinner } from '../../assets/icons/spinner.svg';
import { ReactComponent as Copy } from '../../assets/icons/copy.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as SunIcon } from '../../assets/icons/sun.svg';
import { ReactComponent as MoonIcon } from '../../assets/icons/moon.svg';
const SettingsPage = () => {
  const [addSiteModalVisible, setAddSiteModalVisible] = useState(false);
  const showMessage = useMessage();
  const [popupVisible, setPopupVisible] = useState(false);
  const [apiTokenCopied, setApiTokenCopied] = useState(false);
  const [deleteSiteModalVisible, setDeleteSiteModalVisible] = React.useState(false);
  const [invoicesModalVisible, setInvoicesModalVisible] = React.useState(false);
  const [invoiceTemplateModalVisible, setInvoiceTemplateModalVisible] = React.useState(false);
  const [changeCardModalVisible, setChangeCardModalVisible] = React.useState(false);
  const [cancelSubscriptionModalVisible, setCancelSubscriptionModalVisible] = React.useState(false);
  const [siteIndex, setSiteIndex] = React.useState(0);
  const [subscriptionsModalVisible, setSubscriptionsModalVisible] = React.useState(false);
  const [editedIndex, setEditedIndex] = React.useState(-1);
  const [paymentMethod, setPaymentMethod] = React.useState(null);
  const [paymentVersion, setPaymentVersion] = React.useState(0);
  const [subscription, setSubscription] = React.useState(null);
  const [subscriptionVersion, setSubscriptionVersion] = React.useState(0);
  const [apiToken, setApiToken] = React.useState(null);
  const [isSiteListOverflowing, setIsSiteListOverflowing] = React.useState(false);
  const [showAllSites, setShowAllSites] = React.useState(false);
  const { isMobile } = useMobile();
  const { vw } = useViewport();
  const { themeMode, setThemeMode } = useTheme();
  const siteListRef = useRef(null);
  const useStackedSiteCards = vw <= 640;

  useEffect(() => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-customer-payment-method', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json',
                 'Authorization': localStorage.getItem('token')
               },
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setPaymentMethod(data.paymentMethod);
        }
        else {
          setPaymentMethod({});
        }
      })
      .catch(() => setPaymentMethod({}));
  }, [paymentVersion]);

  useEffect(() => {

    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json',
                 'Authorization': localStorage.getItem('token')
               },
    })
      .then(res => res.json())
      .then(data => {
        setSubscription(data)
      });

  }, [subscriptionVersion]);

  useEffect(() => {
    if (!apiTokenCopied) return undefined;

    const timeoutId = window.setTimeout(() => {
      setApiTokenCopied(false);
    }, 1400);

    return () => window.clearTimeout(timeoutId);
  }, [apiTokenCopied]);

  const { getSites, deleteSite, renameSite, reorderSites } = useSites();
  const sites = getSites();

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    reorderSites(source, destination);
  };

  const cancelSubscription = () => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/cancel-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json',
                 'Authorization': localStorage.getItem('token')
               },
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          // A null cap alone is no longer enough; billing UI now branches on explicit subscription status.
          setSubscription((prev) => (prev ? { ...prev, status: 'canceled' } : { status: 'canceled', cap: null }));
        }
      });
  };

  const getApiToken = () => {
    setApiToken('');

    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-api-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json',
                 'Authorization': localStorage.getItem('token')
               },
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setApiToken(data.token);
        }
      });
  };

  const getCap = (tier) => {
    let { maxTraffic, price } = tiers[tier]

    return Math.round(maxTraffic * price)
  }

  const findTier = (cap) => {
    for (let i = 0; i < tiers.length; i++) {
      if (getCap(i) === cap) {
        return i;
      }
    }
    return 0;
  }

  const getNumClicks = (subscription) => {
    if (subscription && subscription.cap !== undefined) {
      if (subscription.cap === 1000000) {
        return 1000000;
      }
      else {
        return tiers[findTier(subscription.cap)].maxTraffic
      }
    }
    else {
      return 25000;
    }
  }

  const headerGrid = gridTemplateSettings(isMobile, vw);
  const hasPaymentCard = Boolean(paymentMethod?.card);
  const hasActiveSubscription = subscription?.status === 'active';

  useEffect(() => {
    const updateOverflow = () => {
      const element = siteListRef.current;
      if (!element || showAllSites) {
        setIsSiteListOverflowing(false);
        return;
      }
      setIsSiteListOverflowing(element.scrollHeight - element.clientHeight > 1);
    };

    updateOverflow();
    const raf = requestAnimationFrame(updateOverflow);
    window.addEventListener('resize', updateOverflow);
    window.addEventListener('orientationchange', updateOverflow);
    let observer;
    if (typeof ResizeObserver !== 'undefined' && siteListRef.current) {
      observer = new ResizeObserver(updateOverflow);
      observer.observe(siteListRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateOverflow);
      window.removeEventListener('orientationchange', updateOverflow);
      if (observer) observer.disconnect();
    };
  }, [sites.length, showAllSites]);

  return (
    <div className="page-shell page-shell--spacious settings-page">

      {addSiteModalVisible && (
        <AddSiteModal
          setAddSiteModalVisible={setAddSiteModalVisible}
          existingNames={sites.map((site) => site.website)}
        />
      )}

      {editedIndex !== -1 && (
        <EditSiteModal oldDomain={sites[editedIndex].website}
                       setEditedIndex={setEditedIndex}
                       saveDomain={(newDomain) => {
                         renameSite(sites[editedIndex], newDomain);
                       }}
                       existingNames={sites.map(site => site.website)}
        />
      )}

      <h2 className="page-header">Settings</h2>

      {invoicesModalVisible && (
        <DownloadInvoicesModal setInvoicesModalVisible={setInvoicesModalVisible} />
      )}

      {invoiceTemplateModalVisible && (
        <InvoiceTemplateModal setInvoiceTemplateModalVisible={setInvoiceTemplateModalVisible} />
      )}

      {cancelSubscriptionModalVisible && (
        <CancelSubscriptionModal setCancelSubscriptionModalVisible={setCancelSubscriptionModalVisible}
                                 cancelSubscription={cancelSubscription} />
      )}

      {changeCardModalVisible && (
        <ChangeCardModal setChangeCardModalVisible={setChangeCardModalVisible}
                         incrementPaymentVersion={() => setPaymentVersion(paymentVersion + 1)} />
      )}

      {deleteSiteModalVisible && (
        <DeleteSiteModal site={sites[siteIndex]}
                         setDeleteSiteModalVisible={setDeleteSiteModalVisible}
                         deleteSite={deleteSite} />
      )}

      {subscriptionsModalVisible && (
        <SubscriptionsModal setSubscriptionsModalVisible={setSubscriptionsModalVisible}
                            subscription={subscription}
                            incrementSubscriptionVersion={() => setSubscriptionVersion(subscriptionVersion + 1)} />
      )}

      <div className="page-content-shell">
      <div className="section-header__with-buttons">
        <div className="section-title__with-count">
          <h4 className="section-title">Website Management</h4>
          <span className="section-count">{sites.length}</span>
        </div>
        <button
          className="btn primary"
          type="button"
          aria-label="Add website"
          onClick={() => setAddSiteModalVisible(true)}
        >
          <PlusIcon className="icon sm on-primary" />
          <span>Add website</span>
        </button>
      </div>
      <section className="section-box--pad">
        {!useStackedSiteCards && (
          <>
            <ListHeader
              columns={headerGrid}
              cells={[
                null,
                null,
                'URL',
                null,
                vw <= 760 ? 'Fraud' : 'Fraud Level',
                'Status',
                'Actions',
              ]}
            />

            <Separator className="section-divider section-divider--tight" />
          </>
        )}

        {sites.length === 0 && (
          <span className="empty-state">No websites</span>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable" direction="vertical">
            {(provided) => (
              <div className={`website-container settings-site-list${showAllSites ? ' is-expanded' : ''}`}
                   {...provided.droppableProps}
                   ref={(element) => {
                     provided.innerRef(element);
                     siteListRef.current = element;
                   }}
              >
                {sites.map((site, index) => (
                  <div className="sitecard-container"
                       key={site.website}>
                    <Draggable draggableId={site.website}
                               index={index}>
                      {(provided) => (
                        <div ref={provided.innerRef}
                             {...provided.draggableProps}>

                          <SettingsSiteRow site={site}
                                           setDeleteSiteModalVisible={setDeleteSiteModalVisible}
                                           setEditedIndex={setEditedIndex}
                                           index={index}
                                           setSiteIndex={setSiteIndex}
                                           dragHandleProps={provided.dragHandleProps}
                          />
                          <Separator />
                        </div>
                      )}
                    </Draggable>
                  </div>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {isSiteListOverflowing && (
          <button
            type="button"
            className="btn btn-subtle settings-site-list__expand"
            onClick={() => setShowAllSites(true)}
          >
            View all websites
          </button>
        )}
      </section>

      <h4 className="section-header">Subscription</h4>
      <section className="section-box--tight settings-subscription">

        <div className="subscription">
          <PlanCard setSubscriptionsModalVisible={setSubscriptionsModalVisible}
                    subscription={subscription}
                    numClicks={getNumClicks(subscription)}
                    canDownloadInvoices={hasPaymentCard}
                    onDownloadInvoices={() => setInvoicesModalVisible(true)}
                    onEditInvoiceTemplate={() => setInvoiceTemplateModalVisible(true)} />
          <div className="subscription-side">
            <PaymentMethodCard setModalVisible={setChangeCardModalVisible}
                               paymentMethod={paymentMethod} />

            {hasActiveSubscription && (
              <div className="box-gradient subscription-callout">
                <div className="inner gradient subscription-callout__inner">
                  <p className="footnote subscription-callout__note">
                    Please contact{' '}
                    <a href="mailto:team@polygraph.net">
                      team@polygraph.net
                    </a>
                    {' '}for subscription details.
                  </p>

                  <button className="btn destructive"
                          onClick={() => {
                            setCancelSubscriptionModalVisible(true);
                          }}>
                    Cancel Subscription
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <h4 className="section-header">Tokens</h4>
      <section className="section-box--pad">
        <div className='settings-token-actions'>

        {apiToken === null && (
          <button className='btn secondary'
                  onClick={() => getApiToken()}>
            Generate Token
          </button>
        )}

        {apiToken === '' && (
          <div className="spinner">
            <Spinner />
          </div>
        )}

        {apiToken !== null && apiToken !== '' && (
          <div className="settings-token-display">
            <div className="token gradient">
              <p>{apiToken}</p>
            </div>
            <button type="button"
                    className={`btn btn-action${apiTokenCopied ? ' is-confirmed' : ''}`}
                    onClick={async () => {
                      await copyText(apiToken);
                      setApiTokenCopied(true);
                      showMessage('Your API token has been copied successfully.');
                    }}
                    onMouseEnter={() => setPopupVisible(true)}
                    onMouseLeave={() => setPopupVisible(false)}>

              <Copy className="icon base" />

              {popupVisible && (
                <div className="tooltip">
                  {apiTokenCopied ? 'Copied' : 'Copy to Clipboard'}
                </div>
              )}
            </button>
          </div>
        )}
        </div>
      </section>

      <h4 className="section-header">Appearance</h4>
      <section className="section-box--pad theme-mode">
        <div className="theme-mode__options" role="group" aria-label="Theme mode">
          <button
            type="button"
            className={`theme-mode__option${themeMode === 'light' ? ' is-active' : ''}`}
            aria-pressed={themeMode === 'light'}
            onClick={() => setThemeMode('light')}
          >
            <SunIcon className="icon sm" />
            <span>Light mode</span>
          </button>
          <button
            type="button"
            className={`theme-mode__option${themeMode === 'dark' ? ' is-active' : ''}`}
            aria-pressed={themeMode === 'dark'}
            onClick={() => setThemeMode('dark')}
          >
            <MoonIcon className="icon sm" />
            <span>Dark mode</span>
          </button>
        </div>
      </section>
      </div>
    </div>
  );
};

export default SettingsPage;
